import { useState, useEffect } from 'react';
import { supabase, UserRole } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

export type RoleName = UserRole['role'];

const ACTIVE_ROLE_STORAGE_KEY = 'premios_active_role';
const ROLE_PRIORITY: RoleName[] = ['novagob_team', 'jury', 'public'];

const readStoredRole = (): RoleName | null => {
  try {
    const stored = localStorage.getItem(ACTIVE_ROLE_STORAGE_KEY);
    if (stored === 'novagob_team' || stored === 'jury' || stored === 'public') {
      return stored;
    }
    return null;
  } catch {
    return null;
  }
};

const pickInitialActiveRole = (available: RoleName[]): RoleName | null => {
  if (available.length === 0) return null;
  const stored = readStoredRole();
  if (stored && available.includes(stored)) return stored;
  return ROLE_PRIORITY.find((r) => available.includes(r)) ?? available[0];
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [activeRole, setActiveRoleState] = useState<RoleName | null>(null);
  const [loading, setLoading] = useState(true);

  const ensurePublicRole = async () => {
    try {
      const { error } = await supabase.rpc('ensure_premios_public_role');
      if (error) {
        console.error('Error ensuring public role:', error);
      }
    } catch (e) {
      console.error('Error calling ensure_premios_public_role:', e);
    }
  };

  const loadUserRoles = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .schema('premios')
        .from('user_roles')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      const roles = data || [];
      setUserRoles(roles);
      const available = roles.map((r) => r.role as RoleName);
      setActiveRoleState(pickInitialActiveRole(available));
    } catch (error) {
      console.error('Error loading user roles:', error);
      setUserRoles([]);
      setActiveRoleState(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 1. Initial load from existing session (page refresh)
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserRoles(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // 2. Listen only for real auth changes (sign in, sign out)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Skip INITIAL_SESSION since getSession() already handles it
      if (event === 'INITIAL_SESSION') return;

      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserRoles(session.user.id);
      } else {
        setUserRoles([]);
        setActiveRoleState(null);
        try {
          localStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
        } catch {}
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    if (userData.user) {
      await ensurePublicRole();
      await loadUserRoles(userData.user.id);
    }
  };

  const signUp = async (email: string, password: string, metadata: any) => {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: `${metadata.name} ${metadata.lastName || ''}`,
          ...metadata,
          app_source: 'premios',
        },
      },
    });

    if (authError) throw authError;

    if (authData.user) {
      // Identidad core en id.users
      const { error: idUserError } = await supabase
        .schema('id')
        .from('users')
        .upsert({
          id: authData.user.id,
          name: metadata.name,
          last_name: metadata.lastName || null,
          username: metadata.username?.toLowerCase() || null,
          email: email,
          avatar: metadata.avatar || null,
          birth_date: metadata.birthDate || null,
          position: metadata.position || null,
          institution: metadata.department || null,
          job_category: metadata.jobCategory || null,
          administration_type: metadata.administrationType || null,
          country: metadata.country || 'España',
          region: metadata.region || null,
          is_organization: metadata.isOrganization || false,
          app_source: 'premios',
        });

      if (idUserError) {
        console.error('Error creating id.users on register:', idUserError);
      }

      // Fila mínima en public.profiles para compatibilidad con la Red Social
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({ id: authData.user.id, status: 'pending' });

      if (profileError) {
        console.error('Error creating public.profiles stub:', profileError);
      }

      await ensurePublicRole();
    }

    return authData;
  };

  const signOut = async () => {
    try {
      localStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
    } catch {}
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const hasRole = (role: RoleName) => {
    return userRoles.some((r) => r.role === role);
  };

  const availableRoles: RoleName[] = ROLE_PRIORITY.filter((r) =>
    userRoles.some((ur) => ur.role === r)
  );

  const setActiveRole = (role: RoleName) => {
    if (!hasRole(role)) return;
    try {
      localStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, role);
    } catch {}
    setActiveRoleState(role);
  };

  const refreshRoles = async () => {
    if (user?.id) {
      await loadUserRoles(user.id);
    }
  };

  // is* flags reflect the CURRENTLY ACTIVE role (not just ownership).
  // For raw ownership checks use hasRole().
  const isNovagobTeam = activeRole === 'novagob_team';
  const isJury = activeRole === 'jury';
  const isPublic = activeRole === 'public';

  return {
    user,
    userRoles,
    availableRoles,
    activeRole,
    setActiveRole,
    refreshRoles,
    loading,
    signIn,
    signUp,
    signOut,
    hasRole,
    isNovagobTeam,
    isJury,
    isPublic,
  };
};
